import React, { useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import useInstallPrompt from '../../hooks/useInstallPrompt'
import { isInstallDismissedForever, setInstallDismissedForever } from '../../services/pwaInstall'

/**
 * Shows a dialog asking the user to install the PWA after login.
 * Behaviour:
 *   - Never shown when already running in standalone / PWA mode
 *   - Never shown when user checked "don't show again"
 *   - If user clicks "לא" (without checking the box) → dialog reappears next session
 *   - If user checks the box → dialog never reappears
 *   - If user clicks "התקן" → browser native install prompt fires
 *
 * #330: the "never again" flag now lives in `services/pwaInstall`, so the side
 * menu can clear it. Asking for the install option is a retraction of the
 * checkbox, and before this there was no way to retract it at all.
 */
export default function InstallPwaDialog() {
  const { isInstalled, isSupported, promptInstall } = useInstallPrompt()
  const [open, setOpen] = useState(false)
  const [neverShow, setNeverShow] = useState(false)

  useEffect(() => {
    if (isInstalled) return
    if (isInstallDismissedForever()) return
    if (!isSupported) return  // No programmatic install API — don't bother the user
    setOpen(true)
  }, [isInstalled, isSupported])

  if (isInstalled) return null

  const handleInstall = async () => {
    setOpen(false)
    await promptInstall()
  }

  const handleDismiss = () => {
    if (neverShow) {
      setInstallDismissedForever()
    }
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleDismiss}>
      <DialogTitle>הוסף לשולחן העבודה</DialogTitle>
      <DialogContent>
        <DialogContentText>
          ניתן להוסיף את האפליקציה לשולחן העבודה שלך ולפתוח אותה ישירות בלי דפדפן.
        </DialogContentText>
        <FormControlLabel
          sx={{ mt: 1, display: 'flex' }}
          control={
            <Checkbox
              checked={neverShow}
              onChange={(e) => setNeverShow(e.target.checked)}
              size="small"
            />
          }
          label="לא להראות הודעה זאת שוב"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} color="inherit">
          לא
        </Button>
        <Button onClick={handleInstall} variant="contained">
          התקן
        </Button>
      </DialogActions>
    </Dialog>
  )
}
